import { useEffect, useRef } from 'react'
import { useEscapeKey } from '../../lib/useEscapeKey'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'

export type ConfirmVariant = 'danger' | 'warning' | 'info'

interface ConfirmDialogProps {
  isOpen: boolean
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  tertiaryLabel?: string
  variant?: ConfirmVariant
  onConfirm: () => void
  onCancel: () => void
  onTertiary?: () => void
}

const variantStyles: Record<ConfirmVariant, { button: string; icon: string }> = {
  danger: {
    button: 'bg-red-600 hover:bg-red-700 text-white',
    icon: 'text-red-500',
  },
  warning: {
    button: 'bg-amber-600 hover:bg-amber-700 text-white',
    icon: 'text-amber-500',
  },
  info: {
    button: 'bg-cyan-600 hover:bg-cyan-700 text-white',
    icon: 'text-cyan-500',
  },
}

export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tertiaryLabel,
  variant = 'danger',
  onConfirm,
  onCancel,
  onTertiary,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null)

  // Auto-focus confirm button when dialog opens
  useEffect(() => {
    if (isOpen) {
      confirmRef.current?.focus()
    }
  }, [isOpen])

  // Handle escape key
  useEscapeKey(onCancel, isOpen)

  const dialogContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            onClick={onCancel}
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          >
            <div
              className="w-full max-w-sm rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-900 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 ${variantStyles[variant].icon}`}>
                    {variant === 'danger' && (
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    )}
                    {variant === 'warning' && (
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    {variant === 'info' && (
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {title}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-slate-400">
                      {message}
                    </p>
                  </div>
                </div>
              </div>

              <div className={`px-6 pb-6 ${tertiaryLabel ? 'flex flex-col gap-2' : 'flex gap-3'}`}>
                {tertiaryLabel && onTertiary && (
                  <button
                    type="button"
                    onClick={onTertiary}
                    className="w-full px-4 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white font-medium text-sm transition-colors"
                  >
                    {tertiaryLabel}
                  </button>
                )}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 font-medium text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  >
                    {cancelLabel}
                  </button>
                  <button
                    ref={confirmRef}
                    type="button"
                    onClick={onConfirm}
                    className={`flex-1 px-4 py-2.5 rounded-xl font-medium text-sm transition-colors ${variantStyles[variant].button}`}
                  >
                    {confirmLabel}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )

  return createPortal(dialogContent, document.body)
}
